import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { ExecutionResult, IChartCriteria, LookupCriteria, TypeAheadViewModel, VendorLookupViewModel, } from "@tylertech/tyler-api-types";
import { ChartConfigurationCustomTypesPerDataset, ChartEvent, ChartType, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { VendorLookupService } from "../services";

@Component({
    selector: "accounts-payable-vendor-history-chart",
    templateUrl: "./vendorHistoryChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class VendorHistoryChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $vendorLookupService = inject(VendorLookupService);
    private readonly $chartService = inject(ChartService);

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();
    private _chartCriteria: IChartCriteria;

    readonly commands: ReadonlyArray<Command>;
    readonly ChartTitle: string = "Vendor History";
    readonly showChart = signal(false);

    chartConfig: ChartConfigurationCustomTypesPerDataset;
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        const vendorSetting = this.chartCriteria?.Parameters?.find(x => x.Id === "VendorHistoryVendorId");
        const vendor = vendorSetting?.Value;
        if (vendor)
            return vendor.LongDesc;
        return "";
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            (_resolveFn, rejectFn) =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                try
                {
                    this.updateChartConfiguration();
                }
                catch (err)
                {
                    rejectFn(err);
                }
            })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    getVendors(searchText: string): Promise<TypeAheadViewModel[]>
    {
        return this.$vendorLookupService
            .Lookup(<LookupCriteria>{ SearchText: searchText })
            .then((executionResult: ExecutionResult<Array<VendorLookupViewModel>>) =>
            {
                return executionResult.Result.map((item: VendorLookupViewModel): TypeAheadViewModel =>
                {
                    return {
                        Id: item.Id,
                        ShortDesc: item.Number,
                        LongDesc: item.Name,
                    };
                });
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors(3);
        const trendLineColor = this.$chartService.getTrendLineColor();

        this.chartConfig = {
            options: {
                maintainAspectRatio: false,
                responsive: true,
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y0: {
                        type: "linear",
                        display: true,
                        position: "left",
                        // id: "y-axis-0",
                        grid: { display: false },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    y1: {
                        type: "linear",
                        display: true,
                        position: "right",
                        // id: "y-axis-1",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            boxWidth: 12,
                        },
                        onClick: function (e: ChartEvent, legendItem: LegendItem, _legend: LegendElement<ChartType>): void
                        {
                            const index = legendItem.datasetIndex;
                            const ci = this.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.scales[yAxisID].options.display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<ChartType>[]): string | string[] =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<ChartType>): string | string[] =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        }
                    },
                },
                datasets: {
                    bar: {
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,

                    },
                    line: {
                        tension: 0.5,
                        pointStyle: 'line',
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            yAxisID: "y1",
                            backgroundColor: chartColors[0],
                            borderColor: chartColors[0],
                            hoverBackgroundColor: chartColors[0],
                            hoverBorderColor: chartColors[0],
                        },
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            yAxisID: "y1",
                            backgroundColor: chartColors[1],
                            borderColor: chartColors[1],
                            hoverBackgroundColor: chartColors[1],
                            hoverBorderColor: chartColors[1],
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[2].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[2].Data,
                            yAxisID: "y0",
                            borderColor: trendLineColor,
                            backgroundColor: trendLineColor,
                        },
                    ]
                    : [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            yAxisID: "y1",
                            backgroundColor: chartColors[0],
                            borderColor: chartColors[0],
                            hoverBackgroundColor: chartColors[0],
                            hoverBorderColor: chartColors[0],
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            yAxisID: "y0",
                            borderColor: trendLineColor,
                            backgroundColor: trendLineColor,
                            pointHoverBackgroundColor: trendLineColor,
                            pointHoverBorderColor: trendLineColor,
                        },
                    ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
